import {
  type CountryCode,
  defaultCountry,
  findCountryByLocale,
  type LatLng,
  liveCountries,
  type MapAreaId,
  markets,
  type OrganizerMode,
  registeredCountries,
} from "@/config/markets";
import type { LocaleType } from "@/constants/locales";
import type { Coords } from "@/types/places";
import type { TranslationValues } from "use-intl/core";

export type { CountryCode, MapAreaId };

export const DEFAULT_COORDS: Coords = { lat: 59.9139, lon: 10.7522 }; // Oslo

/**
 * Convert Coords (with lon) to Google Maps LatLngLiteral format (with lng)
 */
export function coordsToLatLng(coords: Coords): { lat: number; lng: number } {
  return {
    lat: coords.lat,
    lng: coords.lon,
  };
}

/**
 * Convert Google Maps LatLngLiteral (with lng) to Coords format (with lon)
 */
export function latLngToCoords(latLng: { lat: number; lng: number }): Coords {
  return {
    lat: latLng.lat,
    lon: latLng.lng,
  };
}

/**
 * Get location coordinates from either custom location or predefined area.
 */
export function resolveLocationCoords(
  customLocation: CustomLocation | null,
  areaId: MapAreaId,
): Coords {
  if (customLocation) {
    return { lat: customLocation.lat, lon: customLocation.lon };
  }
  return latLngToCoords(mapAreaCoords[areaId]);
}

/**
 * Coordinates of every registered map area. Display strings are in the i18n
 * locale files under `mapAreas.{areaId}`.
 */
export const mapAreaCoords = Object.fromEntries(
  registeredCountries.flatMap((code) => Object.entries(markets[code].areas)),
) as Record<MapAreaId, LatLng>;

const areaToCountry = Object.fromEntries(
  registeredCountries.flatMap((code) =>
    Object.keys(markets[code].areas).map((area) => [area, code]),
  ),
) as Record<MapAreaId, CountryCode>;

/** The map areas of the live markets, in registry order. */
export const mapAreaIds: MapAreaId[] = liveCountries.flatMap(
  (code) => Object.keys(markets[code].areas) as MapAreaId[],
);

/** Whether `value` is the id of a live map area. */
export function isMapAreaId(
  value: string | null | undefined,
): value is MapAreaId {
  return (mapAreaIds as readonly (string | null | undefined)[]).includes(value);
}

/**
 * Get the country code for a given area ID
 */
export function getAreaCountry(areaId: MapAreaId): CountryCode {
  return areaToCountry[areaId];
}

/**
 * Get all area IDs for a given country code, in display order.
 */
export function getAreaIdsByCountry(countryCode: CountryCode): MapAreaId[] {
  return Object.keys(markets[countryCode].areas) as MapAreaId[];
}

/**
 * Custom location selected via Google Places or geolocation
 */
export interface CustomLocation {
  lat: number;
  lon: number;
  /** Empty for a point without a name; the page then says "your location". */
  displayName: string;
  secondaryText?: string;
}

/**
 * Map an area ID to the closest matching site locale.
 */
export function mapAreaToLocale(areaId: MapAreaId): LocaleType {
  const country = getAreaCountry(areaId);
  return getCountryLocale(country);
}

/**
 * Get the locale for a given country code.
 */
export function getCountryLocale(countryCode: CountryCode): LocaleType {
  return markets[countryCode].locale;
}

/**
 * Get the country code for a given locale.
 */
export function getLocaleCountry(locale: LocaleType): CountryCode {
  return findCountryByLocale(locale) ?? defaultCountry;
}

/**
 * Get the default area (main city) for a given country.
 */
export function getCountryDefaultArea(countryCode: CountryCode): MapAreaId {
  return markets[countryCode].defaultArea;
}

/** The default area of the market that owns `locale`. */
export function getLocaleDefaultArea(locale: LocaleType): MapAreaId {
  return getCountryDefaultArea(getLocaleCountry(locale));
}

export function getLocaleOrganizerMode(locale: LocaleType): OrganizerMode {
  return markets[getLocaleCountry(locale)].organizerMode;
}

/** The currency of the market that owns `locale`. */
export function getLocaleCurrency(locale: LocaleType): string {
  return markets[getLocaleCountry(locale)].currency;
}

/**
 * Calculate distance between two points using the Haversine formula.
 * Returns distance in kilometers.
 */
export function getDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * The area nearest to the coordinates, among `candidates` (the live areas by
 * default).
 */
export function getNearestAreaId(
  userLat: number,
  userLon: number,
  candidates: readonly MapAreaId[] = mapAreaIds,
): MapAreaId {
  let nearestId = candidates[0];
  let minDistance = Number.POSITIVE_INFINITY;

  for (const id of candidates) {
    const coords = mapAreaCoords[id];
    const distance = getDistance(userLat, userLon, coords.lat, coords.lng);
    if (distance < minDistance) {
      minDistance = distance;
      nearestId = id;
    }
  }

  return nearestId;
}

/**
 * Normalize longitude to [-180, 180) range.
 * Handles International Date Line crossing.
 */
function normalizeLongitude(lon: number): number {
  let normalized = lon % 360;
  if (normalized >= 180) {
    normalized -= 360;
  } else if (normalized < -180) {
    normalized += 360;
  }
  return normalized;
}

/**
 * Round latitude or longitude to ~1.1 meter precision.
 */
export function roundCoord(value: number, isLongitude = false): number {
  const normalized = isLongitude ? normalizeLongitude(value) : value;
  return Math.round(normalized * 1e5) / 1e5;
}

/**
 * Round a coordinate object to ~1.1 meter precision.
 * Handles International Date Line crossing for longitude.
 */
export function roundCoords(value: Coords): Coords {
  return {
    lat: roundCoord(value.lat, false),
    lon: roundCoord(value.lon, true),
  };
}

export function formatDistance(
  distanceKm: number,
  locale: LocaleType,
  t?: (key: string, values?: TranslationValues) => string,
): string {
  if (t) {
    if (distanceKm < 0.1) {
      return t("distance.lessThan0_1km");
    }
    if (distanceKm < 10) {
      return t("distance.kmAway", {
        distance: distanceKm.toFixed(1),
      });
    }
    return t("distance.kmAway", {
      distance: Math.round(distanceKm).toString(),
    });
  }

  const isNorwegian = locale === "nb-no";
  if (distanceKm < 0.1) {
    return isNorwegian ? "< 0,1 km unna" : "< 0.1 km away";
  }
  const distance =
    distanceKm < 10 ? distanceKm.toFixed(1) : Math.round(distanceKm).toString();
  return isNorwegian ? `${distance} km unna` : `${distance} km away`;
}
